import { externalConstraint } from '@flatfile/plugin-constraints'
import { recordHook } from '@flatfile/plugin-record-hook'
import { recordkeeperListener } from './listeners'
import {
  formatPlanEin,
  formatPlanSponsor,
  formatPlanName,
  formatPlanNumber,
  formatZipCode,
  formatDob,
  formatSsn,
  formatCountryCode,
  formatState,
  validateDob,
  validateSsn,
  validateEmailAddress,
  validateCountryCodeStateCityZip,
  formatZipForCheck,
  formatAmount,
  formatTpaBdProcessingFee,
  formatInspiraBdProcessingFee,
  formatTransactionType,
  formatPaymentInstructions,
  formatGrossDistributionAndAmount,
  formatCapitalGain,
  formatNetAppreciation,
  formatTotalEmployeeContributions,
  formatAmountAllocable,
  formatStateForCheck,
  formatPayerStateNumber,
  formatTaxableAmount,
  formatTaxableAmountNotDetermined,
  formatTotalDistribution,
  formatEmployeeContributions,
  formatFederalIncomeTax,
  formatFederalIncomeTaxAmount,
  formatPercentageTotalDistribution,
  formatDistributionCode,
  formatStateTaxWithheld,
  formatStateTaxWithheldAmount,
  validatePaymentInstructions,
  validateWireAchFields,
  validateFfcFields,
  validateAccountType,
  validateCheckPayableInfo,
  validateAccountNumber,
  validateAddressForCheck,
  validateZipForCheck,
  validateFederalIncomeTax,
  validateStateTaxWithheld,
} from './participantsDataFormatting'
import { buildBdValidationContext } from './bdValidationContext'

export const bdParticipantListeners = (
  listener,
  selectedPaymentMethod,
  userAudience
) => {
  const ctx = buildBdValidationContext(userAudience)

  // Formatting: single hook runs all field formatters in sequence
  listener.use(
    recordHook('participant-record', (record) => {
      formatPlanEin(record)
      formatPlanSponsor(record)
      formatPlanName(record)
      formatPlanNumber(record)
      formatZipCode(record)
      formatDob(record)
      formatSsn(record)
      formatCountryCode(record)
      formatState(record)
      formatAmount(record)
      formatTpaBdProcessingFee(record)
      formatInspiraBdProcessingFee(record)
      formatTransactionType(record)
      formatPaymentInstructions(record)
      formatGrossDistributionAndAmount(record, selectedPaymentMethod)
      formatCapitalGain(record)
      formatNetAppreciation(record)
      formatTotalEmployeeContributions(record)
      formatAmountAllocable(record)
      formatStateForCheck(record)
      formatZipForCheck(record)
      formatPayerStateNumber(record)
      formatTaxableAmount(record)
      formatTaxableAmountNotDetermined(record)
      formatTotalDistribution(record)
      formatEmployeeContributions(record)
      formatFederalIncomeTax(record)
      formatFederalIncomeTaxAmount(record)
      formatPercentageTotalDistribution(record)
      formatDistributionCode(record)
      formatStateTaxWithheld(record)
      formatStateTaxWithheldAmount(record)
      return record
    })
  )

  // Validation: single hook runs all validators in sequence
  listener.use(
    recordHook('participant-record', (record) => {
      validateDob(record, ctx)
      validateSsn(record, ctx)
      validateEmailAddress(record)
      validateCountryCodeStateCityZip(record, ctx)
      validatePaymentInstructions(record)
      validateWireAchFields(record)
      validateFfcFields(record)
      validateAccountType(record)
      validateCheckPayableInfo(record)
      validateAccountNumber(record)
      validateAddressForCheck(record)
      validateZipForCheck(record)
      validateFederalIncomeTax(record)
      validateStateTaxWithheld(record)
      return record
    })
  )

  recordkeeperListener(listener, 'participant-record')

  listener.use(
    externalConstraint('recordKeeper', (value, key, { record }) => {
      const rules = ctx.recordkeeper
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
    })
  )

  listener.use(
    externalConstraint('tpa', (value, key, { record }) => {
      const rules = ctx.tpa
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (ctx.tpaRequired && value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planSponsor', (value, key, { record }) => {
      const rules = ctx.planSponsor
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.invalidString.re.test(value))
        record.addError(key, rules.invalidString.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planName', (value, key, { record }) => {
      const rules = ctx.planName
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planEin', (value, key, { record }) => {
      const rules = ctx.planEin
      if (value && !rules.invalidEINFormat.re.test(value))
        record.addError(key, rules.invalidEINFormat.error)
      if (value && !rules.invalidEIN.re.test(value))
        record.addError(key, rules.invalidEIN.error)
    })
  )

  listener.use(
    externalConstraint('planNumber', (value, key, { record }) => {
      const rules = ctx.planNumber
      if (value && rules.invalidChars.re.test(value))
        record.addError(key, rules.invalidChars.error)
      if (value && !rules.mustBeThreeDigits.re.test(value))
        record.addError(key, rules.mustBeThreeDigits.error)
    })
  )

  listener.use(
    externalConstraint('firstName', (value, key, { record }) => {
      const rules = ctx.firstName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.namePrefix.re.test(value))
        record.addError(key, rules.namePrefix.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('middleName', (value, key, { record }) => {
      const rules = ctx.middleName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('lastName', (value, key, { record }) => {
      const rules = ctx.lastName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('addressLine1', (value, key, { record }) => {
      const rules = ctx.addressLine1
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && !rules.mustContainNumberAndLetter.re.test(value))
        record.addError(key, rules.mustContainNumberAndLetter.error)
    })
  )

  listener.use(
    externalConstraint('addressLine2', (value, key, { record }) => {
      const { maxChar } = ctx.addressLine2
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('city', (value, key, { record }) => {
      const { maxChar } = ctx.city
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('phone', (value, key, { record }) => {
      const { phoneNumber } = ctx.phone
      if (value && !phoneNumber.re.test(value))
        record.addError(key, phoneNumber.error)
    })
  )

  listener.use(
    externalConstraint('ssn', (value, key, { record }) => {
      const { validityCheck } = ctx.ssn
      if (value && validityCheck.re.test(value))
        record.addError(key, validityCheck.error)
    })
  )

  listener.use(
    externalConstraint('emailAddress', (value, key, { record }) => {
      const { maxChar } = ctx.emailAddress
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('transactionType', (value, key, { record }) => {
      const { validValues } = ctx.transactionType
      if (value && !validValues.re.test(value))
        record.addError(key, validValues.error)
    })
  )

  listener.use(
    externalConstraint('amount', (value, key, { record }) => {
      const { maxChar } = ctx.amount
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint(
      'tpaBenefitDistributionProcessingFee',
      (value, key, { record }) => {
        const { maxChar } = ctx.tpaBenefitDistributionProcessingFee
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint('paymentInstructions', (value, key, { record }) => {
      const { validValues } = ctx.paymentInstructions
      if (value && !validValues.re.test(value))
        record.addError(key, validValues.error)
    })
  )

  listener.use(
    externalConstraint(
      'inspiraBenefitDisbursementProcessingFee',
      (value, key, { record }) => {
        const { maxChar } = ctx.inspiraBenefitDisbursementProcessingFee
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint('grossDistributionAmount', (value, key, { record }) => {
      const { maxChar } = ctx.grossDistributionAmount
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('wireAch', (value, key, { record }) => {
      const { maxChar } = ctx.wireAch
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('abaRouting', (value, key, { record }) => {
      const { format } = ctx.abaRouting
      if (value && !format.re.test(value)) record.addError(key, format.error)
    })
  )

  listener.use(
    externalConstraint('wireAch3', (value, key, { record }) => {
      const { format } = ctx.wireAch3
      if (value && !format.re.test(value)) record.addError(key, format.error)
    })
  )

  listener.use(
    externalConstraint('ffcName', (value, key, { record }) => {
      const { format } = ctx.ffcName
      if (value && !format.re.test(value)) record.addError(key, format.error)
    })
  )

  listener.use(
    externalConstraint('ffcNumber', (value, key, { record }) => {
      const { format } = ctx.ffcNumber
      if (value && !format.re.test(value)) record.addError(key, format.error)
    })
  )

  listener.use(
    externalConstraint('accountType', (value, key, { record }) => {
      const { validValues } = ctx.accountType
      if (value && !validValues.re.test(value))
        record.addError(key, validValues.error)
    })
  )

  listener.use(
    externalConstraint('checkPayableInfo', (value, key, { record }) => {
      const { maxChar } = ctx.checkPayableInfo
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('accountNumber', (value, key, { record }) => {
      const { format } = ctx.accountNumber
      if (value && !format.re.test(value)) record.addError(key, format.error)
    })
  )

  listener.use(
    externalConstraint('addressForCheck', (value, key, { record }) => {
      const { maxChar } = ctx.addressForCheck
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('cityForCheck', (value, key, { record }) => {
      const { maxChar } = ctx.cityForCheck
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('stateForCheck', (value, key, { record }) => {
      const { maxChar } = ctx.stateForCheck
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('zipForCheck', (value, key, { record }) => {
      const { maxChar } = ctx.zipForCheck
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('grossDistribution', (value, key, { record }) => {
      const { maxChar } = ctx.grossDistribution
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('taxableAmount', (value, key, { record }) => {
      const { maxChar } = ctx.taxableAmount
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint(
      'taxableAmountNotDetermined',
      (value, key, { record }) => {
        const { validValues } = ctx.taxableAmountNotDetermined
        if (value && !validValues.re.test(value))
          record.addError(key, validValues.error)
      }
    )
  )

  listener.use(
    externalConstraint('totalDistribution', (value, key, { record }) => {
      const { validValues } = ctx.totalDistribution
      if (value && !validValues.re.test(value))
        record.addError(key, validValues.error)
    })
  )

  listener.use(
    externalConstraint('capitalGain', (value, key, { record }) => {
      const { maxChar } = ctx.capitalGain
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('federalIncomeTax', (value, key, { record }) => {
      const { maxChar } = ctx.federalIncomeTax
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('federalIncomeTaxAmount', (value, key, { record }) => {
      const { maxChar } = ctx.federalIncomeTaxAmount
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('employeeContributions', (value, key, { record }) => {
      const { maxChar } = ctx.employeeContributions
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('netAppreciation', (value, key, { record }) => {
      const { maxChar } = ctx.netAppreciation
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('distributionCode', (value, key, { record }) => {
      const rules = ctx.distributionCode
      if (value && !rules.invalidFormat.re.test(value))
        record.addError(key, rules.invalidFormat.error)
      if (value && !rules.lengthCheck.re.test(value))
        record.addError(key, rules.lengthCheck.error)
    })
  )

  listener.use(
    externalConstraint(
      'percentageTotalDistribution',
      (value, key, { record }) => {
        const { maxChar } = ctx.percentageTotalDistribution
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      'totalEmployeeContributions',
      (value, key, { record }) => {
        const { maxChar } = ctx.totalEmployeeContributions
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint('amountAllocable', (value, key, { record }) => {
      const { maxChar } = ctx.amountAllocable
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('firstYearContrib', (value, key, { record }) => {
      const { format } = ctx.firstYearContrib
      if (value && !format.re.test(value)) record.addError(key, format.error)
    })
  )

  listener.use(
    externalConstraint('stateTaxWithheld', (value, key, { record }) => {
      const { maxChar } = ctx.stateTaxWithheld
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('stateTaxWithheldAmount', (value, key, { record }) => {
      const { maxChar } = ctx.stateTaxWithheldAmount
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('payerStateNumber', (value, key, { record }) => {
      const { maxChar } = ctx.payerStateNumber
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )
}
